import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import DateSelectorV2 from './DateSelectorV2';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Components/Inputs/DateSelectorV2',
  component: DateSelectorV2,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `DateSelectorV2` component provides an intuitive and customizable date picker input field. It supports dynamic date formats, validation, default values, and advanced styling.\n\n',
      // When & how to use it?
      '\nUse the `DateSelectorV2` component in forms or filters where users need to select dates. It is suitable for scenarios that require date validation, custom formats, and responsive UI.\n\n',
      // Current Usage
      `\nThe \`DateSelectorV2\` component is commonly used in:
      \n- Travel booking forms
      \n- Event scheduling
      \n- Report filters\n\n`,
      // Related Components
      `\n- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**: Used as the base input field for the date selector.
       \n- **[Icons](http://localhost:6006/?path=/docs/components-icons-icons--docs)**: Includes the custom calendar icon for visual clarity.\n\n`,
    ),
  },
  decorators: [(story) => <div style={{ padding: '3rem' }}>{story()}</div>],
  tags: ['autodocs'],
  argTypes: {
    label: {
      control: { type: 'text' },
      description: 'The label displayed for the date picker input field.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    name: {
      control: { type: 'text' },
      description: 'The name attribute of the date picker input field, used for form submission.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    value: {
      control: { type: 'string' },
      description: 'The current value of the date picker input, formatted as a string or Date object.\n\n',
      table: {
        type: { summary: 'string | Date' },
        required: false,
      },
    },
    setValue: {
      control: { type: 'function' },
      description: 'Callback function triggered when the date value changes.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    shouldDefaultDate: {
      control: { type: 'boolean' },
      description: 'Determines whether the current date should be set by default if no date is selected.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    isLabelBeside: {
      control: { type: 'boolean' },
      description: 'If true, displays the label beside the date picker input instead of above it.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    isRequired: {
      control: { type: 'boolean' },
      description: 'Marks the input as required for form validation.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    errorMessage: {
      control: { type: 'text' },
      description: 'Error message displayed when the input fails validation.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
  },
};

const SelectDate = ({
  label, name, shouldDefaultDate = false, isLabelBeside = false, isRequired = false, errorMessage,
}) => {
  const [localDate, setLocalDate] = useState('');

  return (
    <div style={{ minHeight: '300px' }}>
      <DateSelectorV2
        label={label}
        name={name}
        value={localDate}
        setValue={setLocalDate}
        shouldDefaultDate={shouldDefaultDate}
        isLabelBeside={isLabelBeside}
        isRequired={isRequired}
        errorMessage={errorMessage}
      />
    </div>
  );
};

const Template = SelectDate.bind({});

export const Default = Template.bind({});
Default.args = { label: '', name: '', shouldDefaultDate: false };

Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the default configuration of the `DateSelectorV2` component without a preselected date or specific styling.\n\n',
  // Where is it currently used on the product?
  '\nThe `DateSelectorV2` component is used in various forms for date selection.\n\n',
);

export const WithoutSelectedDate = Template.bind({});
WithoutSelectedDate.args = { label: 'Arrival', name: 'Arrival' };

WithoutSelectedDate.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when a default date is not required for the `DateSelectorV2` component.\n\n',
  // Where is it currently used on the product?
  '\nThis configuration is often used in forms where a default date is not pre-filled.\n\n',
);

export const WithSelectedDate = Template.bind({});
WithSelectedDate.args = { label: 'Arrival', name: 'Arrival', shouldDefaultDate: true };

WithSelectedDate.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when a default date is required for the `DateSelectorV2` component. Additionally,the date can be displayed in different formats using the dateFormat prop.\n\n',
  // Where is it currently used on the product?
  '\nThis configuration is often used in forms where a default date is pre-filled.\n\n',
);

export const WithoutSelectedDateWithErrorMessage = Template.bind({});
WithoutSelectedDateWithErrorMessage.args = {
  label: 'Arrival', name: 'Arrival', isRequired: true, errorMessage: 'Enter the date',
};

WithoutSelectedDateWithErrorMessage.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to showcase validation errors in the `DateSelectorV2` component.\n\n',
  // Where is it currently used on the product?
  '\nValidation is often required in forms where date input is mandatory.\n\n',
);

export const WithSelectedDateAndLabelBesides = Template.bind({});
WithSelectedDateAndLabelBesides.args = {
  label: 'Arrival', name: 'Arrival', shouldDefaultDate: true, isLabelBeside: true,
};

export const WithoutSelectedDateAndLabelBesides = Template.bind({});
WithoutSelectedDateAndLabelBesides.args = {
  label: 'Arrival', name: 'Arrival', isLabelBeside: true,
};

export const WithoutSelectedDateAndWithoutErrorMessage = Template.bind({});
WithoutSelectedDateAndWithoutErrorMessage.args = {
  label: 'Arrival', name: 'Arrival', isRequired: true,
};

export const WithoutSelectedDateAndLabelBesidesWithErrorMessage = Template.bind({});
WithoutSelectedDateAndLabelBesidesWithErrorMessage.args = {
  label: 'Arrival', name: 'Arrival', isRequired: true, errorMessage: 'Enter the date', isLabelBeside: true,
};

export const WithoutSelectedDateAndLabelBesidesAndWithoutErrorMessage = Template.bind({});
WithoutSelectedDateAndLabelBesidesAndWithoutErrorMessage.args = {
  label: 'Arrival', name: 'Arrival', isRequired: true, isLabelBeside: true,
};

SelectDate.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  shouldDefaultDate: PropTypes.bool,
  isLabelBeside: PropTypes.bool,
  isRequired: PropTypes.bool,
  errorMessage: PropTypes.string,
};
